/**
 * The task pocket (spec §6.5): one surface for a task's details, beside its
 * task on a board or docked at the right of the canvas. The hosts place it
 * (task-pocket-anchored.tsx, task-pocket-dock.tsx) and key it by task; this
 * renders its two pages.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { CSSProperties, KeyboardEvent } from "react";
import { PocketHeader, PocketPanel, StatusDot } from "@/components/ui";
import {
  liveTaskExecution,
  taskStateText,
  taskStateTone,
} from "@/components/shapes/taskboard-frame/task-state";
import { useTaskQuestion } from "@/components/shapes/taskboard-frame/use-task-signals";
import { answerInInputBar } from "@/lib/answer-in-input-bar";
import { translate, useT } from "@/i18n";
import { panelsTasksMessages } from "@/i18n/messages/panels-tasks";
import { useNotificationStore } from "@/stores/notification-store";
import { cn } from "@/lib/cn";
import { openTaskDetails } from "@/lib/right-panel-navigation";
import { useCompanionStore } from "@/stores/companion-store";
import { useTaskPocketStore } from "@/stores/task-pocket-store";
import { useTasksStore } from "@/stores/tasks-store";
import { TaskCommentSection } from "./task-comment-section";
import {
  TaskDescriptionSection,
  TaskTitleSection,
} from "./task-detail-editors";
import { TaskPocketDetails } from "./task-pocket-details";
import { TaskPocketHeader } from "./task-pocket-header";
import { TaskPocketRows } from "./task-pocket-rows";
import {
  useTaskDetailComments,
  useTaskDetailHasDeliverables,
} from "./use-task-detail-data";

/** The pocket reads and writes in the task's own project. */
interface TaskPocketProps {
  taskId: string;
  boardFrameId?: string | null;
  className?: string;
  style?: CSSProperties;
}

export const TaskPocket = forwardRef<HTMLDivElement, TaskPocketProps>(
  function TaskPocket({ taskId, boardFrameId = null, className, style }, ref) {
    const task = useTasksStore((s) =>
      s.tasks.find((item) => item.id === taskId),
    );
    const localTaskComments = useTasksStore((s) => s.localTaskComments[taskId]);
    const refetchSignal = useTasksStore((s) => s.refetchSignal);
    const editTask = useTasksStore((s) => s.editTask);
    const close = useTaskPocketStore((s) => s.close);
    const t = useT(panelsTasksMessages);
    const taskExecution = useCompanionStore((s) =>
      liveTaskExecution(s.executions, taskId),
    );
    const question = useTaskQuestion(taskId);
    const page = useTaskPocketStore((s) => s.page);
    const setPage = useTaskPocketStore((s) => s.setPage);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState("");
    const [isEditingDesc, setIsEditingDesc] = useState(false);
    const [descDraft, setDescDraft] = useState("");
    const titleInputRef = useRef<HTMLInputElement>(null);
    const descInputRef = useRef<HTMLTextAreaElement>(null);
    const [comments, setComments] = useTaskDetailComments(
      task,
      localTaskComments,
      refetchSignal,
    );
    const hasDeliverables = useTaskDetailHasDeliverables(task, refetchSignal);

    useEffect(() => {
      if (isEditingTitle && titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.select();
      }
    }, [isEditingTitle]);

    // Size the description field before paint so edit mode doesn't jump.
    useLayoutEffect(() => {
      const field = descInputRef.current;
      if (!field || !isEditingDesc) return;
      field.style.height = "auto";
      field.style.height = `${field.scrollHeight}px`;
      field.focus();
    }, [isEditingDesc]);

    const handleSaveTitle = useCallback(() => {
      if (!task) return;
      const trimmed = titleDraft.trim();
      if (trimmed && trimmed !== task.title) {
        void editTask(task.project, task.id, { title: trimmed });
      }
      setIsEditingTitle(false);
    }, [editTask, task, titleDraft]);

    const handleSaveDescription = useCallback(() => {
      if (!task) return;
      if (descDraft !== (task.description ?? "")) {
        void editTask(task.project, task.id, { description: descDraft });
      }
      setIsEditingDesc(false);
    }, [descDraft, editTask, task]);

    // Another task (a blocker, or one this task blocks) opens where this pocket sits.
    const openOtherTask = useCallback(
      (otherTaskId: string) =>
        void openTaskDetails(otherTaskId, boardFrameId ? { boardFrameId } : {}),
      [boardFrameId],
    );

    // Escape that a field or menu in the pocket left alone closes it.
    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        event.stopPropagation();
        close();
      },
      [close],
    );

    if (!task) return null;
    const projectId = task.project;
    const running =
      taskExecution?.status === "running" ||
      taskExecution?.status === "waiting_approval";
    const tone = taskStateTone(task, { question: question !== null, running });

    return (
      <PocketPanel
        ref={ref}
        nativeGroup="task-pocket"
        nativeSurface={boardFrameId === null}
        testId="task-pocket"
        role="dialog"
        aria-label={task.ticket_display ?? task.title}
        className={cn("w-[390px]", className)}
        style={style}
        onKeyDown={handleKeyDown}
        data-interactive="true"
      >
        {/* The glass menu clips overflow; its inner body owns scrolling. */}
        <div
          className="scrollbar-none min-h-0 overflow-y-auto overscroll-contain"
          data-testid="task-pocket-scroll"
          data-native-glass-static-scroll="true"
        >
          {page === "details" ? (
            <>
              <PocketHeader
                title={t("moreDetails")}
                onBack={() => setPage("main")}
                onClose={close}
                testIdPrefix="task-pocket-details"
              />
              <TaskPocketDetails
                projectId={projectId}
                task={task}
                onOpenTask={openOtherTask}
              />
            </>
          ) : (
            <>
              <TaskPocketHeader
                projectId={projectId}
                task={task}
                boardFrameId={boardFrameId}
                taskExecution={taskExecution}
                hasDeliverables={hasDeliverables}
                title={
                  <TaskTitleSection
                    title={task.title}
                    isEditingTitle={isEditingTitle}
                    titleDraft={titleDraft}
                    titleInputRef={titleInputRef}
                    setTitleDraft={setTitleDraft}
                    setIsEditingTitle={setIsEditingTitle}
                    onSaveTitle={handleSaveTitle}
                  />
                }
                onClose={close}
              />
              {tone ? (
                <div className="px-2 pt-1.5" data-testid="task-pocket-state">
                  <p className="flex items-center gap-1.5 text-appkit-caption font-medium text-muted">
                    <StatusDot tone={tone} />
                    {taskStateText(task, tone)}
                  </p>
                  {question ? (
                    <button
                      type="button"
                      onClick={() =>
                        void answerInInputBar(question.id).catch(() =>
                          useNotificationStore.getState().addSystemToast({
                            id: "task-answer-error",
                            title: translate(
                              panelsTasksMessages,
                              "questionFailed",
                            ),
                            level: "error",
                          }),
                        )
                      }
                      className="mt-1 block w-full rounded-control px-1.5 py-1 text-left text-appkit-body text-primary transition-colors hover:bg-[var(--color-interactive-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                      data-testid="task-pocket-question"
                    >
                      <span className="line-clamp-3">{question.question}</span>
                    </button>
                  ) : null}
                </div>
              ) : null}
              <TaskDescriptionSection
                description={task.description}
                isEditingDesc={isEditingDesc}
                descDraft={descDraft}
                descInputRef={descInputRef}
                setDescDraft={setDescDraft}
                setIsEditingDesc={setIsEditingDesc}
                onSaveDescription={handleSaveDescription}
              />
              <TaskPocketRows
                projectId={projectId}
                task={task}
                onOpenTask={openOtherTask}
              />
              <TaskCommentSection
                projectId={projectId}
                taskId={task.id}
                comments={comments}
                onCommentsChange={setComments}
              />
            </>
          )}
        </div>
      </PocketPanel>
    );
  },
);
